import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  RefreshCw,
  Send,
  Square,
} from 'lucide-react';

export interface HitlPrompt {
  resume_token?: string;
  user_instruction?: string;
  question?: string;
  explanation?: string;
  options?: string[];
  option_descriptions?: string[];
}

interface HitlPromptCardProps {
  prompt: HitlPrompt;
  onSubmit: (optionId: number, decision: string, reply: string) => Promise<void>;
}

const DRAFT_PREFIX = "The agent's first draft:";

function headline(text?: string): string {
  if (!text) return 'The build pipeline needs your input.';
  if (text.includes('Agent agent.code-generator produced a response.')) {
    return 'The code generator finished its first draft. How should we continue?';
  }
  if (text.includes('produced a response. How should I proceed?')) {
    return 'A checkpoint has been reached. Review the output and choose how to continue.';
  }
  return text;
}

function optionIcon(opt: string) {
  const o = opt.toLowerCase();
  if (o.includes('accept')) return CheckCircle2;
  if (o.includes('stop')) return Square;
  if (o.includes('angle')) return RefreshCw;
  return Pencil;
}

/**
 * One-click HITL decision card: choosing an option submits it immediately
 * (no separate "Submit" step), the action area stays pinned at the bottom of
 * the card, the agent draft is collapsed by default, and number keys 1–N pick
 * an option.
 */
export function HitlPromptCard({ prompt, onSubmit }: HitlPromptCardProps) {
  const [reply, setReply] = useState('');
  const [pendingIdx, setPendingIdx] = useState<number | null>(null);
  const [showDraft, setShowDraft] = useState(false);

  const options = prompt.options ?? [];
  const draft = (prompt.explanation ?? '').replace(`${DRAFT_PREFIX}\n\n`, '').trim();
  const hasDraft = draft !== '' && draft !== DRAFT_PREFIX;
  const draftLines = hasDraft ? draft.split('\n').length : 0;
  const acceptIdx = Math.max(0, options.findIndex((o) => o.toLowerCase().includes('accept')));
  const busy = pendingIdx !== null;

  const choose = async (idx: number, decision: string) => {
    if (busy) return;
    setPendingIdx(idx);
    try {
      await onSubmit(idx, decision, reply.trim());
    } finally {
      setPendingIdx(null);
    }
  };

  // Number-key shortcuts (ignored while typing in the instructions box).
  useEffect(() => {
    if (options.length === 0) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT')) return;
      const n = Number(e.key);
      if (Number.isInteger(n) && n >= 1 && n <= options.length) {
        e.preventDefault();
        void choose(n - 1, options[n - 1]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, reply, busy]);

  const secondary = options.map((opt, idx) => ({ opt, idx })).filter(({ idx }) => idx !== acceptIdx);

  return (
    <div className="flex flex-col w-full max-w-2xl max-h-[85vh] bg-[#131722]/95 backdrop-blur-xl border border-primary-500/30 rounded-2xl shadow-[0_0_50px_-10px_rgba(139,92,246,0.25)] animate-fade-in overflow-hidden">
      {/* Header + question */}
      <div className="px-6 pt-5 pb-4 border-b border-[#2a2e3d]">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary-400 mb-2">
          <AlertTriangle className="w-4 h-4" /> Action required
        </div>
        <p className="text-white text-lg font-semibold leading-snug">
          {headline(prompt.user_instruction || prompt.question)}
        </p>
      </div>

      {/* Scrollable body: collapsible draft */}
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar px-6 py-4">
        {hasDraft && (
          <div className="rounded-xl border border-white/5 bg-black/30">
            <button
              type="button"
              onClick={() => setShowDraft((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-300 hover:text-white"
            >
              <span className="flex items-center gap-2">
                {showDraft ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                {showDraft ? 'Hide agent draft' : 'Review agent draft'}
              </span>
              <span className="text-xs text-gray-500">{draftLines} lines</span>
            </button>
            {showDraft && (
              <pre className="px-4 pb-4 max-h-[40vh] overflow-auto text-xs text-gray-300 font-mono whitespace-pre-wrap custom-scrollbar">
                {draft}
              </pre>
            )}
          </div>
        )}
        {!hasDraft && prompt.explanation && (
          <p className="text-sm text-gray-400 whitespace-pre-wrap">{prompt.explanation}</p>
        )}
      </div>

      {/* Pinned action area — always visible, no scrolling needed */}
      <div className="border-t border-[#2a2e3d] bg-[#101320] px-6 py-4 flex flex-col gap-3">
        <textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          onKeyDown={(e) => {
            if (options.length === 0 && e.key === 'Enter' && (e.metaKey || e.ctrlKey) && reply.trim()) {
              e.preventDefault();
              void choose(0, '');
            }
          }}
          rows={2}
          disabled={busy}
          placeholder={options.length ? 'Optional: add instructions to send with your choice…' : 'Type your reply…'}
          className="w-full resize-none bg-[#1a1d27] border border-[#2a2e3d] rounded-xl px-4 py-2.5 text-sm text-white placeholder-gray-600 focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all disabled:opacity-60"
        />

        {options.length > 0 ? (
          <>
            <button
              type="button"
              onClick={() => choose(acceptIdx, options[acceptIdx])}
              disabled={busy}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-success-500 hover:brightness-110 text-white font-bold text-base shadow-[0_0_20px_rgba(34,197,94,0.25)] transition-all disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {pendingIdx === acceptIdx ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
              {options[acceptIdx]}
              <kbd className="ml-2 text-[10px] font-mono px-1.5 py-0.5 rounded bg-black/25 text-white/70">{acceptIdx + 1}</kbd>
            </button>

            {secondary.length > 0 && (
              <div className={`grid gap-2 ${secondary.length >= 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
                {secondary.map(({ opt, idx }) => {
                  const Icon = optionIcon(opt);
                  const isStop = opt.toLowerCase().includes('stop');
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => choose(idx, opt)}
                      disabled={busy}
                      title={prompt.option_descriptions?.[idx]}
                      className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                        isStop
                          ? 'border-red-500/30 text-red-300 hover:bg-red-500/10 hover:border-red-500/60'
                          : 'border-[#2a2e3d] text-gray-200 hover:bg-primary-500/10 hover:border-primary-500/60'
                      }`}
                    >
                      {pendingIdx === idx ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                      <span className="truncate">{opt.replace(/^revise:\s*/i, '')}</span>
                      <kbd className="text-[10px] font-mono px-1 rounded bg-white/5 text-gray-500">{idx + 1}</kbd>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-gray-500 text-center">Click an option or press its number key — it's sent immediately.</p>
          </>
        ) : (
          <button
            type="button"
            onClick={() => choose(0, '')}
            disabled={busy || !reply.trim()}
            className="self-end flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-600 hover:bg-primary-500 text-white font-bold transition-all disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send
            <span className="text-[10px] font-mono text-white/60">Ctrl+Enter</span>
          </button>
        )}
      </div>
    </div>
  );
}
